import React from 'react';
import { match } from 'ts-pattern';

import { CountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';

export interface PaymentMethod {
  currency: CurrencyCodeType;
  accountType: FiatAccountType;
}

const makePaymentMethod = (
  currency: CurrencyCodeType,
  accountType: FiatAccountType,
): PaymentMethod => ({
  currency,
  accountType,
});

export const paymentMethodsByCountry = (
  country: CountryType,
  currency: CurrencyCodeType = CurrencyCodeType.USD,
): ReadonlyArray<PaymentMethod> =>
  match<CountryType, ReadonlyArray<PaymentMethod>>(country)
    .with(CountryType.unset, (): ReadonlyArray<PaymentMethod> => [])
    .with(
      CountryType.US,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.ach),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.wire),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
      ],
    )
    .with(
      CountryType.BR,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.BRL, FiatAccountType.pix),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
      ],
    )
    .with(
      CountryType.MX,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.MXN, FiatAccountType.spei),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
      ],
    )
    .with(
      CountryType.CO,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.COP, FiatAccountType.cop),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
      ],
    )
    .with(
      CountryType.AR,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.ARS, FiatAccountType.cvu),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
      ],
    )
    .with(
      CountryType.SG,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
      ],
    )
    .otherwise(
      (): ReadonlyArray<PaymentMethod> => [makePaymentMethod(currency, FiatAccountType.swift)],
    );

/**
 * Get payment methods available for a specific country and currency.
 * @param country The country to get payment methods for.
 * @param currency The currency to use for the default payment method.
 * @returns An array of payment methods available for the specified country and currency.
 */
export const usePaymentMethodsByCountry = (
  country: CountryType,
  currency: CurrencyCodeType = CurrencyCodeType.USD,
): ReadonlyArray<PaymentMethod> =>
  React.useMemo(
    (): ReadonlyArray<PaymentMethod> => paymentMethodsByCountry(country, currency),
    [country, currency],
  );
